/**
 * Copyright 2017 Lukasz K. (https://github.com/istrib)
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge,
 * publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE
 * OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * vuex-typescript@3.0.1 (https://github.com/istrib/) よりコピーした。
 */
import { ActionContext, Store } from 'vuex'

const useRootNamespace = { root: true }

export function Handler(target: any, key: string) {
  target[key]._vuexKey = key
}

/**
 * Vuex getter handler specified in Vuex options.
 */
export type GetterHandler<TModuleState, TRootState, TResult> =
  (state: TModuleState, rootState: TRootState) => TResult;

/**
 * Vuex action handler which takes payload as specified in Vuex options.
 */
export type ActionHandlerWithPayload<TModuleState, TRootState, TPayload, TResult> =
  (injectee: ActionContext<TModuleState, TRootState>, payload: TPayload) => void | Promise<TResult>;
/**
 * Vuex action handler which does not take payload as specified in Vuex options.
 */
export type ActionHandlerNoPayload<TModuleState, TRootState, TResult> =
  (injectee: ActionContext<TModuleState, TRootState>) => void | Promise<TResult>;

/**
 * Vuex mutation handler which takes payload as specified in Vuex options.
 */
export type MutationHandlerWithPayload<TModuleState, TPayload> =
  (state: TModuleState, payload: TPayload) => void;
/**
 * Vuex mutation handler which does not take payload as specified in Vuex options.
 */
export type MutationHandlerNoPayload<TModuleState> =
  (state: TModuleState) => void;

/**
 * Function which gets value of a concrete Vuex getter.
 */
export type GetAccessor<TModuleState, TRootState, TResult> =
  (store: Store<TRootState> | ActionContext<TModuleState, TRootState>) => TResult;

/**
 * Function which dispatches a concrete Vuex action with payload.
 */
export type DispatchAccessorWithPayload<TModuleState, TRootState, TPayload, TResult> =
  (store: Store<TRootState> | ActionContext<TModuleState, TRootState>, payload: TPayload) => Promise<TResult>;
/**
 * Function which dispatches a concrete Vuex action without payload.
 */
export type DispatchAccessorNoPayload<TModuleState, TRootState, TResult> =
  (store: Store<TRootState> | ActionContext<TModuleState, TRootState>) => Promise<TResult>;

/**
 * Function which commits a concrete Vuex mutation with payload.
 */
export type CommitAccessorWithPayload<TModuleState, TRootState, TPayload> =
  (store: Store<TRootState> | ActionContext<TModuleState, TRootState>, payload: TPayload) => void;
/**
 * Function which commits a concrete Vuex mutation without payload.
 */
export type CommitAccessorNoPayload<TModuleState, TRootState> =
  (store: Store<TRootState> | ActionContext<TModuleState, TRootState>) => void;

export interface StoreAccessors<TModuleState, TRootState> {
  /**
   * Returns a function committing mutations directed to the specified mutation handler.
   * This overload is for handlers which do not expect payload.
   */
  commit(
    handler: MutationHandlerNoPayload<TModuleState>, functionName?: string):
    CommitAccessorNoPayload<TModuleState, TRootState>;

  /**
   * Returns a function committing mutations directed to the specified mutation handler.
   * This overload is for handlers which expect payload.
   */
  commit<TPayload>(
    handler: MutationHandlerWithPayload<TModuleState, TPayload>, functionName?: string):
    CommitAccessorWithPayload<TModuleState, TRootState, TPayload>;

  /**
   * Returns a function dispatching actions directed to the specified action handler.
   * This overload is for handlers which do not expect payload.
   */
  dispatch<TResult>(
    handler: ActionHandlerNoPayload<TModuleState, TRootState, TResult>, functionName?: string):
    DispatchAccessorNoPayload<TModuleState, TRootState, TResult>;

  /**
   * Returns a function dispatching actions directed to the specified action handler.
   * This overload is for handlers which expect payload.
   */
  dispatch<TPayload, TResult>(
    handler: ActionHandlerWithPayload<TModuleState, TRootState, TPayload, TResult>, functionName?: string):
    DispatchAccessorWithPayload<TModuleState, TRootState, TPayload, TResult>;

  /**
   * Returns a function returning value of the specified getter.
   */
  read<TResult>(
    handler: GetterHandler<TModuleState, TRootState, TResult>, functionName?: string):
    GetAccessor<TModuleState, TRootState, TResult>;
}

export function getStoreAccessors<TModuleState, TRootState>(
  namespace: string): StoreAccessors<TModuleState, TRootState> {
  return {
    commit: (handler: Function, functionName?: string) => createAccessor('commit', handler, namespace, functionName),
    dispatch: (handler: Function, functionName?: string) => createAccessor('dispatch', handler, namespace, functionName),
    read: (handler: Function, functionName?: string) => {
      const key = qualifyKey(handler, namespace, functionName)
      return (store: any) => {
        return store.rootGetters
          ? store.rootGetters[key] // ActionContext
          : store.getters[key] // Store
      }
    }
  }
}

function createAccessor(
  operation: string,
  handler: Function,
  namespace: string,
  functionName?: string): any {
  const key = qualifyKey(handler, namespace, functionName)
  return (store: any, payload: any) => {
    return store[operation](key, payload, useRootNamespace)
  }
}

function qualifyKey(handler: Function, namespace?: string, functionName?: string) {
  const key = functionName || (<any>handler).name || (<any>handler)._vuexKey
  if (!key) {
    throw new Error('Vuex handler functions must not be anonymous. '
      + 'Vuex needs a key by which it identifies a handler. '
      + 'If you define handler as class member you must decorate it with @Handler.')
  }
  return namespace
    ? `${namespace}/${key}`
    : key
}
